import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PortalLayout from "@/components/portal/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { playSuccess } from "@/lib/sound";
import { ServiceChips } from "@/components/marketing/ContactForm";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const STATUS_STYLES = {
  "Documents Pending": "bg-orange-400/15 text-orange-300 border-orange-400/30",
  "Filed": "bg-sky-400/15 text-sky-300 border-sky-400/30",
  "Under Examination": "bg-gold/15 text-gold border-gold/30",
  "Published": "bg-violet-400/15 text-violet-300 border-violet-400/30",
  "Opposition": "bg-red-400/15 text-red-300 border-red-400/30",
  "Granted": "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  "Registered": "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  "Renewed": "bg-teal-400/15 text-teal-300 border-teal-400/30",
  "Pending": "bg-gold/15 text-gold border-gold/30",
  "Approved": "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  "Declined": "bg-red-400/15 text-red-300 border-red-400/30",
};

export const StatusBadge = ({ status }) => (
  <span data-testid={`status-badge-${status.toLowerCase().replace(/\s+/g, "-")}`}
    className={`inline-block px-3 py-1 text-xs border rounded-full ${STATUS_STYLES[status] || "bg-white/10 text-mist border-white/20"}`}>
    {status}
  </span>
);

export default function ClientDashboard() {
  const [filings, setFilings] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reqServices, setReqServices] = useState([]);
  const [reqNote, setReqNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/filings").then((r) => setFilings(r.data)).catch(() => setFilings([]));
    api.get("/service-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const granted = (filings || []).filter((f) => ["Granted", "Registered", "Renewed"].includes(f.status)).length;

  const toggleService = (s) =>
    setReqServices((list) => (list.includes(s) ? list.filter((x) => x !== s) : [...list, s]));

  const submitRequest = async (e) => {
    e.preventDefault();
    if (reqServices.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setSending(true);
    try {
      await api.post("/service-requests", { services: reqServices, note: reqNote });
      toast.success("Request sent. Our team will review and confirm shortly.");
      playSuccess();
      setReqServices([]);
      setReqNote("");
      load();
    } catch (err) {
      toast.error(err.response
        ? formatApiErrorDetail(err.response?.data?.detail)
        : "Having trouble reaching the server — please try again in a few seconds.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PortalLayout title="My Filings" subtitle="Live status of every patent and trademark we are prosecuting for you.">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
        {[
          ["Total filings", filings?.length ?? "—"],
          ["In progress", filings ? filings.length - granted : "—"],
          ["Granted / Registered", filings ? granted : "—"],
        ].map(([label, value]) => (
          <div key={label} data-testid={`stat-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
            className="bg-navy-900 border border-white/10 p-6 hover:border-gold/40 transition-colors duration-300">
            <p className="font-serif text-4xl text-gold">{value}</p>
            <p className="text-xs uppercase tracking-[0.2em] text-mist mt-2">{label}</p>
          </div>
        ))}
      </div>

      <div className="bg-navy-900 border border-white/10 overflow-x-auto mb-10">
        {filings === null ? (
          <p className="p-8 text-mist text-sm">Loading filings...</p>
        ) : filings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-mist mb-6" data-testid="filings-empty">No filings on your account yet. Once we file for you, everything appears here.</p>
            <Link to="/" data-testid="filings-empty-cta" className="inline-block px-6 py-3 border border-gold/60 text-gold text-sm hover:bg-gold hover:text-navy-950 transition-colors">
              Request your first filing
            </Link>
          </div>
        ) : (
          <Table data-testid="filings-table">
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {["Reference", "Type", "Title", "Jurisdiction", "Status", "Last update"].map((h) => (
                  <TableHead key={h} className="text-gold/80 uppercase text-xs tracking-wider">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filings.map((f) => (
                <TableRow key={f.id} data-testid={`filing-row-${f.reference || f.id}`} className="border-white/5 hover:bg-navy-800/40">
                  <TableCell className="text-gold font-medium text-sm">{f.reference || "—"}</TableCell>
                  <TableCell className="text-white text-sm">{f.type}</TableCell>
                  <TableCell className="text-mist text-sm max-w-[240px] truncate">{f.title}</TableCell>
                  <TableCell className="text-mist text-sm">{f.jurisdiction || "—"}</TableCell>
                  <TableCell><StatusBadge status={f.status} /></TableCell>
                  <TableCell className="text-mist text-xs">{new Date(f.updated_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submitRequest} data-testid="service-request-form" className="bg-navy-900 border border-gold/20 p-8">
          <h2 className="font-serif text-2xl text-white mb-2">Need another service?</h2>
          <p className="text-mist text-sm mb-6">Select any additional services — our team reviews every request and confirms after discussion.</p>
          <ServiceChips selected={reqServices} onToggle={toggleService} testIdPrefix="request-service-chip" />
          <textarea rows={3} data-testid="service-request-note" placeholder="Anything we should know? (optional)"
            value={reqNote} onChange={(e) => setReqNote(e.target.value)}
            className="w-full mt-6 bg-transparent border-b-2 border-gold/30 focus:border-gold outline-none py-3 text-white placeholder:text-mist/50 transition-colors duration-300 text-sm resize-none" />
          <button type="submit" disabled={sending} data-testid="service-request-submit"
            className="mt-6 px-8 py-3.5 bg-gold text-navy-950 font-semibold text-sm tracking-wide hover:bg-gold-light transition-colors duration-300 disabled:opacity-50">
            {sending ? "Sending..." : "Request Services"}
          </button>
        </form>

        <div className="bg-navy-900 border border-white/10 p-8">
          <h2 className="font-serif text-2xl text-white mb-6">My service requests</h2>
          {requests.length === 0 ? (
            <p className="text-mist text-sm" data-testid="requests-empty">No additional service requests yet.</p>
          ) : (
            <div className="flex flex-col gap-4" data-testid="requests-list">
              {requests.map((r) => (
                <div key={r.id} data-testid={`request-row-${r.id}`} className="border border-white/10 p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-white text-sm">{r.services.join(", ")}</p>
                    {r.note && <p className="text-mist text-xs mt-1">{r.note}</p>}
                    <p className="text-mist/60 text-xs mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
